
import React, { useEffect, useState } from "react";
import axios from "axios";
import styles from "./ResetPassword.module.scss";
import Loader from "../UiComponents/loader/Loader";
import { FaChevronRight } from "react-icons/fa";

import { handleValidation, strongRegex } from "../../utils/formValidation";
import logoHS from "../../pics/logo-hearingspace.png";
import ContainerForm from "../UiComponents/forms/ContainerForm";
import InputPassword from "../UiComponents/forms/InputPassword";
import BtnForms from "../UiComponents/forms/BtnForms";
import RulesPassword from "../UiComponents/forms/RulesPassword";

import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const ResetPassword = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [errorValidation, setErrorValidation] = useState(null);

  // ✅ CHANGEMENT: écran de validation
  const [validatingLink, setValidatingLink] = useState(true);

  const [loading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [displayForms, setDisplayForms] = useState(false);

  const [resetToken, setResetToken] = useState(null);
  const [resetId, setResetId] = useState(null);

  const getTokenAndIdFromHash = () => {
    // 🔐 Lecture du token depuis le fragment (#) de l’URL (non envoyé au serveur)
      // 🔐 Lecture sécurisée du token depuis le fragment (#) de l’URL.
// Le fragment n’est JAMAIS envoyé au serveur HTTP (contrairement aux query params),
// ce qui évite toute fuite du token via les logs, le referer ou les proxies.
// On retire le "#" pour pouvoir parser facilement les paramètres.
    const rawHash = (window.location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(rawHash);

    return {
      token: params.get("token"),
      id: params.get("id"),
      hasSensitiveInUrl: Boolean(params.get("token") || params.get("id")),
    };
  };

  useEffect(() => {
    const run = async () => {
      setValidatingLink(true);

      const { token, id, hasSensitiveInUrl } = getTokenAndIdFromHash();

      if (!token || !id) {
        toast.error(t("password_reset.invalid_link"));
        setDisplayForms(false);
        setValidatingLink(false);
        return;
      }

      // ✅ Stocke token/id avant nettoyage
      setResetToken(token);
      setResetId(id);

      // 🔐 Nettoie immédiatement l’URL (supprime le token visible sans recharger)
      if (hasSensitiveInUrl) {
           // 🔐 Sécurité : on nettoie immédiatement l’URL pour supprimer le token visible.
// Cette opération modifie l’URL affichée dans le navigateur SANS recharger la page.
// Cela empêche les fuites de token via l’historique, les captures d’écran,
// le copier-coller ou les headers HTTP (Referer).
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      try {
        // ✅ Vérification UX (pas une sécurité): le backend confirme que le token est valide/non expiré
        const res = await axios.post(`${process.env.REACT_APP_API_URL}api/verifyToken`, {
          data: { token, id },
        });

        if (res.data?.ok) {
          setDisplayForms(true);
        } else {
          toast.error(t("password_reset.invalid_or_expired_link"));
          setDisplayForms(false);
        }
      } catch (e) {
        toast.error(t("password_reset.invalid_or_expired_link"));
        setDisplayForms(false);
      } finally {
        setValidatingLink(false);
      }
    };

    run();
  }, []);

  const submitChangePassword = async (e) => {
    e.preventDefault();

    if (!resetToken || !resetId) {
      toast.error(t("password_reset.invalid_link"));
      return;
    }

    if (password !== password2) {
      toast.error(t("validation.passwords_not_match"));
      return;
    }

    if (!strongRegex.test(password)) {
      toast.error(t("validation.password_not_strong"));
      return;
    }

    setLoading(true);

    try {
      const res = await axios.put(`${process.env.REACT_APP_API_URL}api/resetPassword`, {
        data: { token: resetToken, id: resetId, password, password2 },
      });

      setLoading(false);

      if (res.data?.error) {
        toast.error(res.data.message || t("password_reset.invalid_or_expired_link"));
        setDisplayForms(false);
        return;
      }

      toast.success(res.data.message || t("password_reset.success"));
      setShowSuccessMessage(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      console.error(err);
      setLoading(false);
      toast.error(t("password_reset.error_occurred"));
      setDisplayForms(false);
    }
  };

  return (
    <>
      {(loading || validatingLink) && <Loader />}

      {showSuccessMessage ? (
        <div className={styles.successMessage}>
          <img src={logoHS} alt="Hearing Space Logo" />
          <h2>{t("password_reset.success_title")}</h2>
          <p>
            {t("password_reset.success_message")}
          </p>
        </div>
      ) : displayForms && !loading && !validatingLink ? (
        <div className="fixed-container-modals">
          <img className={styles.logo} src={logoHS} alt="Hearing Space Logo" />

          <ContainerForm
            onSubmit={submitChangePassword}
            modal={true}
            children={
              <>
                <InputPassword
                  value={password}
                  onChange={(e) => setPassword(e.target.value.trim())}
                  placeholder={t("password_reset.new_password_placeholder")}
                  onKeyUp={(e) => setErrorValidation(handleValidation(e))}
                />

                <InputPassword
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value.trim())}
                  placeholder={t("password_reset.new_password_placeholder")}
                />

                <BtnForms text={t("password_reset.reset_button")} svg={<FaChevronRight />} />
                <RulesPassword />
              </>
            }
          />
        </div>
      ) : !validatingLink ? (
        <div className={styles.error_token}>
          <img src={logoHS} alt="Hearing Space Logo" />
          <p>
            {t("password_reset.error_message")}
          </p>
          <a href={process.env.REACT_APP_HOME}>{t("password_reset.home_link")}</a>
        </div>
      ) : null}
    </>
  );
};

export default ResetPassword;
